import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {PageComponent} from 'features/core/shared/mvs-page/page.component';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {PageHeaderConfig} from 'features/core/shared/service/page/page-header-config.interface';
import {
    MvsConfigHeaderComponent
} from 'features/core/shared/components/mvs-config-header/mvs-config-header.component';

/**
* Standalone Vertragsabrechnung page for ONE contract.
*
* Routed entry point (cn/contract/:contractId/commission) onto the reusable, read-only
* CnContractCommissionComponent (selector mvs-cn-contract-commission). The page reads the
* contractId from the route param and binds it into the component, which loads + renders the
* chronological commission statement lines (AP/BP/Sonstige badges + summary tile) via its own
* CnContractStatementApiService.
*
* The :contractId param is read reactively (route.paramMap) so navigating between two contracts
* WITHOUT leaving the page (same routed component instance) still re-binds the new id and the child
* reloads via its own ngOnChanges. The header runs in Component-Mode (MvsConfigHeaderComponent,
* 'Provisionsabrechnung') with a cross-link back to the Provisions-Dashboard.
*
* The page owns ONLY the routed shell + header + param resolution; all data loading and display live
* in the hosted CnContractCommissionComponent.
*/
@Component({
    selector: 'mvs-cn-contract-commission-page',
    templateUrl: './cn-contract-commission.page.html',
    styleUrls: ['./cn-contract-commission.page.scss'],
    standalone: false
})
export class CnContractCommissionPage extends PageComponent implements OnInit, OnDestroy {

    defaultLabel: string = 'Vertragsabrechnung';

    /** The contract whose Vertragsabrechnung is displayed (from the :contractId route param). */
    contractId: number | null = null;

    /** Subscription to the :contractId route param (re-binds on in-place contract switch). */
    private paramMapSubscription: Subscription;

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        this.defaultLabel = 'Vertragsabrechnung';
        super.ngOnInit();

        // Read the :contractId route param reactively so an in-place contract switch re-binds the id.
        this.paramMapSubscription = this.route.paramMap.subscribe(params => {
            this.contractId = this.parseContractId(params.get('contractId'));
        });

        this.initialized = true;
    }

    override ngOnDestroy(): void {
        if (this.paramMapSubscription) {
            this.paramMapSubscription.unsubscribe();
        }
        super.ngOnDestroy();
    }

    getPageHeaderConfig(): PageHeaderConfig {
        return {
            component: MvsConfigHeaderComponent,
            inputs: {
                header: 'Provisionsabrechnung',
                categoryTypeMode: 'contract',
                icon: 'fa-light fa-file-invoice-dollar',
                links: [
                    {label: 'Dashboard', icon: 'fa-light fa-gauge', url: 'cn/dashboard'},
                    {label: 'Konfiguration', icon: 'fa-light fa-coins', url: 'cn/config'}
                ]
            }
        };
    }

    /**
    * Parse the raw :contractId route param into a numeric id, or null when absent / not a number.
    */
    private parseContractId(raw: string | null): number | null {
        if (raw === null || raw.trim().length === 0) {
            return null;
        }
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    }
}
